
import { GraphElement } from "../graph-element.js"
import { createFromJSON } from "./help.js"
import Konva from "konva"

class FoldLineElement extends GraphElement {
  static _createFunc = createFromJSON(this)
  static async fromJSON(topology, node, complete) {
    const elem = await this._createFunc(topology, node, complete)
    // 在 onLoad 回调中设置方向和尺寸，确保 keyShape 已创建
    // 添加安全检查，防止 node 为 undefined
    if (node) {
      elem.onLoad(() => {
        if (node.horizontalDirection) {
          elem.setHorizontalDirection(node.horizontalDirection)
        }
        if (node.verticalDirection) {
          elem.setVerticalDirection(node.verticalDirection)
        }
        if (node.lineWidth !== undefined) {
          elem.setLineWidth(node.lineWidth)
        }
        if (node.lineHeight !== undefined) {
          elem.setLineHeight(node.lineHeight)
        }
      })
    }
    return elem
  }

  constructor(topology, x, y, attrs) {
    super(topology, "foldLine", x, y, {
      text: "折线",
      port: true,
      ...attrs
    })
    // 水平方向属性：'left' 或 'right'，默认为 'right'
    // 垂直方向属性：'up' 或 'down'，默认为 'down'
    // 添加安全检查，防止 attrs 为 undefined
    this.horizontalDirection = (attrs && attrs.horizontalDirection) ? attrs.horizontalDirection : 'right'
    this.verticalDirection = (attrs && attrs.verticalDirection) ? attrs.verticalDirection : 'down'
    // 线宽和线高属性，默认为 40 和 20
    this.lineWidth = (attrs && attrs.lineWidth) ? attrs.lineWidth : 40
    this.lineHeight = (attrs && attrs.lineHeight) ? attrs.lineHeight : 20
    // 底座加载完成时调用
    this.onLoad(() => {
      // 不需要显示名称
      this.label.visible(false)
    })
  }

  addTransformer() {
    const { storage, editor } = this.__topology

    if (!editor) return

    let tr = this.layer.findOne('#base-el-tr')
    if (!tr) {
      tr = new Konva.Transformer({
        rotateEnabled: false,
        padding: 5,
        ignoreStroke: true,
        id: 'base-el-tr'
      })
      this.layer.add(tr)
    }
    this.keyShape.on('click', () => {
      storage.select(this)
      tr.nodes([this.keyShape])
      this.layer.draw()
    })
    this.stage.on('click', (event) => {
      if (event.target === this.stage) {
        tr.nodes([])
        storage.deselect()
      }
    })

    // 变换时保持端口的相对位置
    this.keyShape.on('transform', () => {
      this.updatePortPosition()
    })

  }

  updatePortPosition() {
    if (!this.keyShape || !this.ports) return
    
    let x = this.keyShape.x()
    let y = this.keyShape.y()
    // Konva.Line 使用 getClientRect() 获取边界框来计算宽度和高度
    const rect = this.keyShape.getClientRect()
    if (!rect) return
    
    let w = rect.width * this.keyShape.scaleX()
    let h = rect.height * this.keyShape.scaleY()
    this.ports.update(x, y, w, h)
  }


  addKeyShape(name, attrs) {
    // 确保属性已初始化（防止在构造函数中 super() 调用时属性还未设置）
    if (this.horizontalDirection === undefined || this.verticalDirection === undefined) {
      this.horizontalDirection = (attrs && attrs.horizontalDirection) ? attrs.horizontalDirection : 'right'
      this.verticalDirection = (attrs && attrs.verticalDirection) ? attrs.verticalDirection : 'down'
    }
    if (this.lineWidth === undefined) {
      this.lineWidth = (attrs && attrs.lineWidth) ? attrs.lineWidth : 40
    }
    if (this.lineHeight === undefined) {
      this.lineHeight = (attrs && attrs.lineHeight) ? attrs.lineHeight : 20
    }
    
    this.keyShape = this._createDarthNode()
    this.g.add(this.keyShape)

    setTimeout(() => {
      this.executeLoadedHook(this.keyShape)
    })
  }

  _createDarthNode() {
    // 直接创建 Konva.Line 作为 keyShape
    // 使用实例的 lineWidth 和 lineHeight 属性
    const w = this.lineWidth || 40
    const h = this.lineHeight || 20
    // 根据水平和垂直方向设置不同的 points
    // 确保方向有默认值，防止未初始化的情况
    const horizontal = this.horizontalDirection || 'right'
    const vertical = this.verticalDirection || 'down'
    const points = this._calculatePoints(w, h, horizontal, vertical)
    const line = new Konva.Line({
      points,
      stroke: '#2CD563',
      strokeWidth: 2,
      lineCap: 'round',
      lineJoin: 'round',
      // 在变换时不会缩放边框
      strokeScaleEnabled: false,
      x: 0,
      y: 0
    })

    return line
  }

  /**
   * 根据水平和垂直方向计算折线的 points
   * @param {number} w - 线宽
   * @param {number} h - 线高
   * @param {string} horizontal - 水平方向：'left' 或 'right'
   * @param {string} vertical - 垂直方向：'up' 或 'down'
   * @returns {Array} points 数组
   */
  _calculatePoints(w, h, horizontal, vertical) {
    // 4种组合：
    // right-up: 从右上角开始，向下到右下角，然后向左到左下角 [w, 0, w, h, 0, h]
    // right-down: 从右下角开始，向上到右上角，然后向左到左上角 [w, h, w, 0, 0, 0]
    // left-up: 从左上角开始，向下到左下角，然后向右到右下角 [0, 0, 0, h, w, h]
    // left-down: 从左下角开始，向上到左上角，然后向右到右上角 [0, h, 0, 0, w, 0]
    if (horizontal === 'right' && vertical === 'up') {
      return [w, 0, w, h, 0, h]
    } else if (horizontal === 'right' && vertical === 'down') {
      return [w, h, w, 0, 0, 0]
    } else if (horizontal === 'left' && vertical === 'up') {
      return [0, 0, 0, h, w, h]
    } else { // left-down
      return [0, h, 0, 0, w, 0]
    }
  }

  /**
   * 更新折线的 points（根据当前的 lineWidth、lineHeight 和方向）
   */
  _updatePoints() {
    if (!this.keyShape) return
    
    const w = this.lineWidth || 40
    const h = this.lineHeight || 20
    const horizontal = this.horizontalDirection || 'right'
    const vertical = this.verticalDirection || 'down'
    const points = this._calculatePoints(w, h, horizontal, vertical)
    this.keyShape.points(points)
    
    // 更新端口位置
    this.updatePortPosition()
    
    // 如果 layer 存在，则重绘
    if (this.layer) {
      this.layer.draw()
    }
  }

  /**
   * 设置水平方向
   * @param {string} horizontal - 水平方向：'left' 或 'right'
   */
  setHorizontalDirection(horizontal) {
    if (horizontal !== 'left' && horizontal !== 'right') {
      console.warn('水平方向值必须是 "left" 或 "right"')
      return
    }
    this.horizontalDirection = horizontal
    this._updatePoints()
  }

  /**
   * 设置垂直方向
   * @param {string} vertical - 垂直方向：'up' 或 'down'
   */
  setVerticalDirection(vertical) {
    if (vertical !== 'up' && vertical !== 'down') {
      console.warn('垂直方向值必须是 "up" 或 "down"')
      return
    }
    this.verticalDirection = vertical
    this._updatePoints()
  }

  /**
   * 设置折线宽度
   * @param {number} lineWidth - 线宽值
   */
  setLineWidth(lineWidth) {
    if (typeof lineWidth !== 'number' || lineWidth <= 0) {
      console.warn('线宽必须是大于 0 的数字')
      return
    }
    this.lineWidth = lineWidth
    this._updatePoints()
  }

  /**
   * 设置折线高度
   * @param {number} lineHeight - 线高值
   */
  setLineHeight(lineHeight) {
    if (typeof lineHeight !== 'number' || lineHeight <= 0) {
      console.warn('线高必须是大于 0 的数字')
      return
    }
    this.lineHeight = lineHeight
    this._updatePoints()
  }

  /**
   * 序列化为 JSON
   */
  toJSON() {
    return {
      ...super.toJSON(),
      horizontalDirection: this.horizontalDirection,
      verticalDirection: this.verticalDirection,
      lineWidth: this.lineWidth,
      lineHeight: this.lineHeight,
    }
  }

}

export { FoldLineElement }
